/**
 * PWA lifecycle helpers — runs on the main thread (not the SW).
 *
 * Exposes:
 *   - registerServiceWorker(): bootstraps the SW + wires auto-update UX.
 *   - listenForInstallPrompt(): captures the beforeinstallprompt event
 *     so we can trigger it from a custom "Install app" button later.
 *   - listenForSwMessages(): handles `postMessage` from the SW after a
 *     notification click so we can route to the target URL.
 *   - reportPwaStatus(): POSTs a small beacon to /api/cl/pwa/status so
 *     the admin console can show who's installed + has push granted.
 */

import { pb } from "./pb";
import { pbFor } from "./api/router";

// ---- beforeinstallprompt ---------------------------------------------------

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const installListeners = new Set<(available: boolean) => void>();

export function listenForInstallPrompt() {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    installListeners.forEach((fn) => fn(true));
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installListeners.forEach((fn) => fn(false));
    reportPwaStatus({ installed: true });
  });
}

export function onInstallAvailabilityChange(fn: (available: boolean) => void): () => void {
  installListeners.add(fn);
  fn(deferredPrompt !== null);
  return () => installListeners.delete(fn);
}

export async function triggerInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferredPrompt) return "unavailable";
  await deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  installListeners.forEach((fn) => fn(false));
  return choice.outcome;
}

/** True when the app is running standalone — i.e. launched from the
 *  home screen / installed shortcut, not a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS-specific legacy flag
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

// ---- SW message bridge -----------------------------------------------------

export function listenForSwMessages(navigate: (url: string) => void) {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.addEventListener("message", (e) => {
    const data = e.data as { type?: string; url?: string } | null;
    if (data?.type === "navigate" && data.url) {
      try {
        const url = new URL(data.url);
        navigate(url.pathname + url.search + url.hash);
      } catch {
        navigate(data.url);
      }
    }
  });
}

// ---- Install + push status beacon -----------------------------------------

export interface PwaStatus {
  installed?: boolean;
  pushPermission?: NotificationPermission;
  /** A stable per-device id so the admin console can list devices. */
  deviceId?: string;
  /** User-agent snippet for the admin list — truncated server-side. */
  ua?: string;
}

const DEVICE_ID_KEY = "cl.deviceId";

export function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

export async function reportPwaStatus(patch: Partial<PwaStatus> = {}): Promise<void> {
  if (!pb.authStore.isValid) return;
  const body: PwaStatus = {
    installed: isStandalone(),
    pushPermission:
      typeof Notification !== "undefined" ? Notification.permission : "default",
    deviceId: getDeviceId(),
    ua: navigator.userAgent.slice(0, 180),
    ...patch,
  };
  try {
    await pbFor("notifications").send("/api/cl/pwa/status", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    /* silent — best-effort beacon */
  }
}

/**
 * Start a live-presence heartbeat. Pings `/api/cl/pwa/status` every
 * 60 seconds while the tab is visible + authenticated, bumping the
 * `pwa_devices.updated` timestamp so the Team page's "Live now" pill
 * reflects actual activity.
 *
 * Returns a cleanup function the caller can use to stop the timer
 * (e.g. on unmount in a root effect). Calling `startPresenceHeartbeat`
 * more than once is safe — it's idempotent via a module-level flag.
 */
let heartbeatTimer: number | null = null;
let heartbeatVisHandler: (() => void) | null = null;

export function startPresenceHeartbeat(intervalMs = 60_000): () => void {
  if (typeof window === "undefined") return () => {};
  if (heartbeatTimer !== null) return stopPresenceHeartbeat;

  const tick = () => {
    if (document.visibilityState !== "visible") return;
    void reportPwaStatus();
  };

  // Fire one immediately so the user appears live within a second of
  // opening the app, then on every interval.
  tick();
  heartbeatTimer = window.setInterval(tick, intervalMs);

  // Also tick on visibility change so returning from a backgrounded
  // tab refreshes the status without waiting for the interval.
  heartbeatVisHandler = () => {
    if (document.visibilityState === "visible") tick();
  };
  document.addEventListener("visibilitychange", heartbeatVisHandler);

  return stopPresenceHeartbeat;
}

export function stopPresenceHeartbeat(): void {
  if (heartbeatTimer !== null) {
    window.clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  if (heartbeatVisHandler) {
    document.removeEventListener("visibilitychange", heartbeatVisHandler);
    heartbeatVisHandler = null;
  }
}
